import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Step,
  Stepper,
  StepLabel,
  Card,
  CardContent,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import "./CybersourcePaymentModal.css";
import LockIcon from "@mui/icons-material/Lock";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import {
  processPayment,
  recordUnifiedCheckoutPayment,
  markPaymentLifecycle,
  formatAmount,
  generateTransactionReference,
  createUnifiedCheckoutCaptureContext,
  loadUnifiedCheckoutScript,
  extractCompleteJwtFromUcResult,
  PAYMENT_DECLINED_USER_MESSAGE,
} from "../../services/cybersourceService";

function formatSeatSummary(selectedSeats, seatGender) {
  if (!selectedSeats?.length) return "";
  return selectedSeats
    .map((seat) => {
      const g = seatGender?.[seat] ?? seatGender?.[String(seat)] ?? "";
      const label = g
        ? `${String(g).charAt(0).toUpperCase()}${String(g).slice(1).toLowerCase()}`
        : "—";
      return `Seat ${seat} (${label})`;
    })
    .join(", ");
}

const CybersourcePaymentModal = ({
  open,
  onClose,
  bookingDetails,
  amount,
  selectedSeats = [],
  seatGender = {},
  onPaymentSuccess,
  onPaymentError,
  onCardEntryTimeout,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /** Seconds left on "Enter Card Details" step; null when not on that step. */
  const [cardSecondsLeft, setCardSecondsLeft] = useState(null);
  const cardTimeoutFiredRef = useRef(false);
  const [transientTokenJwt, setTransientTokenJwt] = useState("");
  /** Same code sent in capture-context and payment (clientReferenceInformation). */
  const [ucSessionReference, setUcSessionReference] = useState("");
  /** True only while fetching capture context + loading the UC SDK (before iframe renders). */
  const [ucBootstrapping, setUcBootstrapping] = useState(false);
  /** True once the UC iframe is mounted in the DOM (so we can hide the spinner). */
  const [ucMounted, setUcMounted] = useState(false);
  /** True once UC returned a transient token (user finished entering payment details). */
  const [ucReady, setUcReady] = useState(false);
  const ucInitRef = useRef(false);
  /** UnifiedPayments instance — used for `complete(transientToken)` when capture context includes completeMandate. */
  const ucUpRef = useRef(null);
  /** Server returned flag: capture context was built with completeMandate (UC-orchestrated sale). */
  const [clientCompleteEnabled, setClientCompleteEnabled] = useState(false);
  /** After UC returns a token, while `complete()` + record run (client-orchestrated path only). */
  const [ucCompleting, setUcCompleting] = useState(false);
  /** UC SDK exposes `complete()` (matches server `completeMandate`). */
  const [sdkSupportsUcComplete, setSdkSupportsUcComplete] = useState(false);
  /**
   * Client orchestration failed or declined; offer classic Next → Confirm → Pay (server REST) with same token.
   */
  const [serverFinishAfterUcFailure, setServerFinishAfterUcFailure] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const useShortStepper =
    clientCompleteEnabled && sdkSupportsUcComplete && !serverFinishAfterUcFailure;
  const steps = useMemo(
    () => {
      if (useShortStepper) {
        return isMobile
          ? ["Review", "Checkout"]
          : ["Review details", "Secure checkout"];
      }
      return isMobile
        ? ["Review", "Card", "Confirm"]
        : ["Review details", "Card details", "Confirm payment"];
    },
    [useShortStepper, isMobile],
  );

  useEffect(() => {
    if (!open || activeStep !== 1) {
      setCardSecondsLeft(null);
      cardTimeoutFiredRef.current = false;
      return undefined;
    }
    cardTimeoutFiredRef.current = false;
    setCardSecondsLeft(300);
    const iv = setInterval(() => {
      setCardSecondsLeft((prev) => {
        if (prev == null || prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [open, activeStep]);

  useEffect(() => {
    if (!open || activeStep !== 1 || cardSecondsLeft === null) return;
    if (cardSecondsLeft > 0) return;
    if (cardTimeoutFiredRef.current) return;
    cardTimeoutFiredRef.current = true;
    onCardEntryTimeout?.();
  }, [open, activeStep, cardSecondsLeft, onCardEntryTimeout]);

  useEffect(() => {
    if (open) return;
    setActiveStep(0);
    setTransientTokenJwt("");
    setUcSessionReference("");
    setUcBootstrapping(false);
    setUcMounted(false);
    setUcReady(false);
    ucInitRef.current = false;
    ucUpRef.current = null;
    setClientCompleteEnabled(false);
    setUcCompleting(false);
    setSdkSupportsUcComplete(false);
    setServerFinishAfterUcFailure(false);
    setError("");
    setCardSecondsLeft(null);
    cardTimeoutFiredRef.current = false;
  }, [open]);

  const formatCountdown = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const launchUnifiedCheckout = async () => {
    setUcBootstrapping(true);
    setUcMounted(false);
    setError("");
    setTransientTokenJwt("");
    setUcSessionReference("");
    try {
      const ucReference = generateTransactionReference();
        setUcSessionReference(ucReference);
      const uc = await createUnifiedCheckoutCaptureContext({
        targetOrigin: window.location.origin,
        amount,
        currency: "LKR",
        firstName: bookingDetails?.first_name,
        lastName: bookingDetails?.last_name,
        customerName: bookingDetails?.passenger_name,
        customerEmail: bookingDetails?.email,
        customerPhone: bookingDetails?.mobile_no,
        billingRecord: bookingDetails,
        reference: ucReference,
      });
      if (bookingDetails?.id) {
        try {
          await markPaymentLifecycle({
            bookingId: bookingDetails.id,
            paymentStatus: "pending",
            detail: "uc_form_loaded",
            reference: ucReference,
            releaseSeats: false,
          });
        } catch {
          /* non-fatal */
        }
      }
      if (!uc?.success || !uc?.captureContext) {
        throw new Error(PAYMENT_DECLINED_USER_MESSAGE);
      }
      setClientCompleteEnabled(uc.clientCompleteEnabled !== false);
      await loadUnifiedCheckoutScript(uc.captureContext);
      if (typeof window.Accept !== "function") {
        throw new Error(PAYMENT_DECLINED_USER_MESSAGE);
      }
      const accept = await window.Accept(uc.captureContext);
      const up = await accept.unifiedPayments(false);
      ucUpRef.current = up;
      setSdkSupportsUcComplete(typeof up.complete === "function");
      // Bootstrap is done; iframe will render now. Hide the spinner.
      setUcBootstrapping(false);
      setUcMounted(true);

      const tokenResponse = await up.show({
        containers: {
          paymentSelection: "#uc-payment-selection",
          paymentScreen: "#uc-payment-screen",
        },
      });

      /**
       * Modern UC SDK resolves with the transient-token JWT as a STRING.
       * Older / wrapper variants resolve with an object with transientToken / transientTokenJwt.
       */
      let token = "";
      if (typeof tokenResponse === "string") {
        token = tokenResponse;
      } else if (tokenResponse && typeof tokenResponse === "object") {
        token =
          tokenResponse.transientTokenJwt ||
          tokenResponse.transientToken ||
          tokenResponse.token ||
          tokenResponse.jwt ||
          "";
      }

      if (!token) {
        throw new Error(PAYMENT_DECLINED_USER_MESSAGE);
      }
      setTransientTokenJwt(token);

      /**
       * With `completeMandate`, CyberSource expects `up.complete(token)` immediately after `show()`
       * resolves (see UC “with Sale” example). Deferring until a later “Pay Now” step often leaves
       * the UC iframe stuck on loading after the in-flow “Confirm” button.
       */
      const serverClientComplete = uc.clientCompleteEnabled !== false;
      if (serverClientComplete && typeof up.complete === "function") {
        if (!bookingDetails?.id) {
          throw new Error("Booking ID is missing. Please complete booking before payment.");
        }
        setUcCompleting(true);
        try {
          try {
            await markPaymentLifecycle({
              bookingId: bookingDetails.id,
              paymentStatus: "confirm_pending",
              detail: "uc_complete_started",
              reference: ucReference,
              releaseSeats: false,
            });
          } catch (markErr) {
            console.warn("[Cybersource] confirm_pending mark failed:", markErr);
          }
          const completeResult = await up.complete(token);
          const completeJwt = extractCompleteJwtFromUcResult(completeResult);
          if (!completeJwt && typeof completeResult !== "object") {
            throw new Error(PAYMENT_DECLINED_USER_MESSAGE);
          }
          const paymentResponse = await recordUnifiedCheckoutPayment({
            bookingId: bookingDetails.id,
            reference: ucReference,
            completeResponseJwt: completeJwt || completeResult,
          });
          if (paymentResponse.success) {
            onPaymentSuccess({
              transactionId: paymentResponse.transactionId,
              authCode: paymentResponse.authCode,
              reference: ucReference,
              amount,
            });
            handleClose("success");
            return;
          }
          setServerFinishAfterUcFailure(true);
          setError(paymentResponse.message || PAYMENT_DECLINED_USER_MESSAGE);
          onPaymentError?.(paymentResponse.message || PAYMENT_DECLINED_USER_MESSAGE);
          setUcReady(true);
        } catch (completeErr) {
          setServerFinishAfterUcFailure(true);
          setError(PAYMENT_DECLINED_USER_MESSAGE);
          onPaymentError?.(PAYMENT_DECLINED_USER_MESSAGE);
          setUcReady(true);
        } finally {
          setUcCompleting(false);
        }
        return;
      }

      setUcReady(true);
    } catch (err) {
      setUcBootstrapping(false);
      setUcMounted(false);
      setUcReady(false);
      setUcCompleting(false);
      setTransientTokenJwt("");
      setUcSessionReference("");
      ucUpRef.current = null;
      setClientCompleteEnabled(false);
      setSdkSupportsUcComplete(false);
      setServerFinishAfterUcFailure(false);
      const detail =
        err?.response?.data?.message ||
        (err?.message && /required|postal|province|billing/i.test(err.message)
          ? err.message
          : null);
      setError(detail || PAYMENT_DECLINED_USER_MESSAGE);
    }
  };

  useEffect(() => {
    if (!open || activeStep !== 1 || ucInitRef.current) return;
    ucInitRef.current = true;
    launchUnifiedCheckout();
  }, [open, activeStep]);

  const handleNextStep = () => {
    if (activeStep === 1) {
      if (!transientTokenJwt) {
        setError("Complete Unified Checkout payment method selection first.");
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    setActiveStep((prev) => prev - 1);
    setError("");
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError("");

      // Check for bookingId
      if (!bookingDetails?.id) {
        setError("Booking ID is missing. Please complete booking before payment.");
        setLoading(false);
        return;
      }

      if (!ucSessionReference) {
        setError("Unified Checkout session is missing. Go back and complete card entry.");
        setLoading(false);
        return;
      }

      const reference = ucSessionReference;

      /**
       * UC client-orchestrated sale (`completeMandate`) is finalized in `launchUnifiedCheckout`
       * right after `show()` so the SDK does not stall. This step only runs the server REST path.
       */
      const paymentResponse = await processPayment({
        amount: Math.round(amount * 100), // Convert to cents
        currency: "LKR",
        reference,
        customerEmail: bookingDetails.email,
        firstName: bookingDetails.first_name,
        lastName: bookingDetails.last_name,
        customerName: bookingDetails.passenger_name,
        customerPhone: bookingDetails.mobile_no,
        transientTokenJwt,
        bookingId: bookingDetails.id,
      });

      if (paymentResponse.success) {
        onPaymentSuccess({
          transactionId: paymentResponse.transactionId,
          authCode: paymentResponse.authCode,
          reference,
          amount,
        });
        handleClose("success");
      } else {
        setError(paymentResponse.message || PAYMENT_DECLINED_USER_MESSAGE);
        onPaymentError(paymentResponse.message || "Payment failed");
        setActiveStep(1);
      }
    } catch (err) {
      setError(PAYMENT_DECLINED_USER_MESSAGE);
      onPaymentError(PAYMENT_DECLINED_USER_MESSAGE);
      setActiveStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (closeReason = "dismiss") => {
    setActiveStep(0);
    setTransientTokenJwt("");
    setUcSessionReference("");
    setUcReady(false);
    setUcBootstrapping(false);
    setUcMounted(false);
    ucInitRef.current = false;
    ucUpRef.current = null;
    setClientCompleteEnabled(false);
    setUcCompleting(false);
    setSdkSupportsUcComplete(false);
    setServerFinishAfterUcFailure(false);
    setError("");
    onClose?.(closeReason);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, muiReason) => handleClose(muiReason || "dismiss")}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEnforceFocus
      disableAutoFocus
      PaperProps={{
        sx: isMobile
          ? { display: "flex", flexDirection: "column" }
          : undefined,
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: { xs: "1rem", sm: "1.25rem" },
          py: { xs: 1.5, sm: 2 },
          pr: 2,
        }}
      >
        <LockIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
        <Box component="span" sx={{ lineHeight: 1.3 }}>
          {isMobile ? "Secure Payment" : "Secure Payment - Cybersource (HNB)"}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          px: { xs: 2, sm: 3 },
          overflowY: "auto",
          flex: isMobile ? 1 : undefined,
        }}
      >
        {/* Stepper */}
        <Stepper
          activeStep={Math.min(activeStep, Math.max(0, steps.length - 1))}
          alternativeLabel={isMobile}
          sx={{
            mb: 3,
            "& .MuiStepLabel-label": {
              fontSize: { xs: "0.7rem", sm: "0.875rem" },
              mt: isMobile ? 0.5 : 0,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Step 1: Review Details */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Booking Summary
            </Typography>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography color="textSecondary">Passenger Name</Typography>
                    <Typography variant="body1">
                      {bookingDetails.passenger_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color="textSecondary">Email</Typography>
                    <Typography variant="body1">
                      {bookingDetails.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color="textSecondary">Phone</Typography>
                    <Typography variant="body1">
                      {bookingDetails.mobile_no}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color="textSecondary">From</Typography>
                    <Typography variant="body1">{bookingDetails.from}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color="textSecondary">To</Typography>
                    <Typography variant="body1">{bookingDetails.to}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography color="textSecondary">Seats</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatSeatSummary(selectedSeats, seatGender) || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="h6">Total Amount:</Typography>
                      <Typography variant="h5" color="primary" sx={{ fontWeight: "bold" }}>
                        {formatAmount(amount)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <Alert severity="info">
              You will be charged {formatAmount(amount)} for this booking.
            </Alert>
          </Box>
        )}

        {/* Step 2: Unified Checkout */}
        {activeStep === 1 && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CreditCardIcon color="primary" />
              Payment Method
            </Typography>

            {cardSecondsLeft != null && cardSecondsLeft > 0 && !transientTokenJwt && !ucCompleting && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please complete your payment details within <strong>{formatCountdown(cardSecondsLeft)}</strong>.
              </Alert>
            )}

            <Card variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
              <CardContent
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                }}
              >
                <Box
                  id="uc-payment-selection"
                  sx={{
                    width: "100%",
                    minHeight: 64,
                    mb: ucReady ? 2 : 0,
                  }}
                />
                <Box
                  id="uc-payment-screen"
                  sx={{
                    // Keep space while UC renders + while `up.complete()` runs.
                    // Collapsing this container can cause device fingerprint submission to fail,
                    // which leads to ECI=0 / payer-auth enrollment issues.
                    width: "100%",
                    minHeight:
                      ucMounted || ucCompleting || ucReady
                        ? { xs: 320, sm: 240 }
                        : 0,
                    borderRadius: 1,
                    transition: "min-height 200ms ease",
                  }}
                />
                {ucBootstrapping && !ucMounted && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 4,
                      color: "text.secondary",
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading secure payment form…</Typography>
                  </Box>
                )}
                {ucCompleting && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 3,
                      color: "text.secondary",
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="body2">Processing payment…</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {transientTokenJwt && !ucCompleting && !useShortStepper && (
              <Alert severity="success">
                Your payment details are saved securely. Click <strong>Next</strong> to continue.
              </Alert>
            )}

            {!ucBootstrapping && error && !transientTokenJwt && (
                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" onClick={launchUnifiedCheckout}>
                      Retry
                    </Button>
                  }
                >
                  We couldn’t load the payment form. Please retry.
                </Alert>
            )}
          </Box>
        )}

        {/* Step 3: Confirm Payment */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: "center" }}>
            <LockIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Confirm Your Payment
            </Typography>
            <Card variant="outlined" sx={{ my: 2 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount to be Charged
                </Typography>
                <Typography variant="h4" color="primary" sx={{ fontWeight: "bold" }}>
                  {formatAmount(amount)}
                </Typography>
              </CardContent>
            </Card>
            <Alert severity="info" sx={{ mt: 2 }}>
              Click <strong>Pay Now</strong> to complete your booking.
            </Alert>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 2 },
          px: { xs: 2, sm: 3 },
          gap: 1,
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          "& > button": {
            width: { xs: "100%", sm: "auto" },
            m: 0,
          },
        }}
      >
        <Button onClick={() => handleClose("cancel")} disabled={loading || ucCompleting}>
          Cancel
        </Button>

        {activeStep > 0 && (
          <Button onClick={handlePreviousStep} disabled={loading || ucCompleting}>
            Back
          </Button>
        )}

        {activeStep < 2 &&
          !(useShortStepper && activeStep === 1) && (
          <Button
            onClick={handleNextStep}
            variant="contained"
            disabled={
              loading ||
              ucCompleting ||
              (activeStep === 1 &&
                (!transientTokenJwt || ucBootstrapping || !ucReady))
            }
          >
            Next
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            onClick={handlePayment}
            variant="contained"
            color="success"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Processing..." : "Pay Now"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CybersourcePaymentModal;
